import { priceDateKey } from '@/utils/circuits/prices/priceDateKey';
import CircuitDetailsImagesCard from '@/components/distributorCircuits/CircuitDetailsImagesCard';
import CircuitDetailsItineraryCard from '@/components/distributorCircuits/CircuitDetailsItineraryCard';
import { CircuitDetailsParticipantsCard } from '@/components/distributorCircuits/CircuitDetailsParticipantsCard';
import CircuitDetailsSummaryCard from '@/components/distributorCircuits/CircuitDetailsSummaryCard';
import EstimateDetailsAndOptions from '@/components/estimates/EstimateDetailsAndOptions';
import StyledButton from '@/components/styled/StyledButton';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '@/components/styled/StyledFormHeaderWithBreadcrumb';
import StyledStepper from '@/components/styled/StyledStepper';
import HorizontalLine from '@/components/ui/HorizontalLine';
import Loader from '@/components/ui/Loader';
import { useCircuitPrices } from '@/hooks/useCircuitPrices';
import {
  useAcceptReservationRequest,
  useGetIncomingReservationRequest,
  useRefuseReservationRequest,
} from '@/hooks/useEstimate';
import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import {
  EstimateFormData,
  EstimateSchema,
} from '@/types/estimates/EstimateFormData';
import { EstimateResponse } from '@/types/estimates/EstimateResponse';
import { computeBenefitPrices } from '@/utils/circuits/prices/computeBenefitPrices';
import { roundUpPrice } from '@/utils/circuits/prices/formatPrice';
import { getDatesToPrice } from '@/utils/circuits/prices/pricingHorizon';
import { loadEstimateDefaultValues } from '@/utils/estimates/loadEstimateDefaultValues';
import { zodResolver } from '@hookform/resolvers/zod';
import { addDays, differenceInYears, startOfToday } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import ReservationSummaryPage from '@/components/reservations/ReservationSummary';
import { useReservationSummary } from '@/hooks/useReservationSummary';
import { useToast } from '@/hooks/useToast';
import { SelectedEstimateOption } from '@/types/estimates/SelectedEstimateOptionType';
import { useTranslation } from 'react-i18next';
import { CircuitFormData } from '@/types/circuits/CircuitFormData';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslatedCountry } from '../../../../../shared/CountriesData';
import { PATHS } from '@/constants/Paths';
import { formatEstimateRef } from '@/utils/displayCodes';

const ViewReservationRequestPage = () => {
  const { estimateId = '' } = useParams();

  const {
    data: estimateResponse,
    isLoading,
    isSuccess,
  } = useGetIncomingReservationRequest(estimateId);

  return isLoading ? (
    <Loader />
  ) : (
    isSuccess && (
      <ReservationRequestPage
        estimateResponse={estimateResponse}
        key={estimateResponse.id}
      />
    )
  );
};

export default ViewReservationRequestPage;

const RESERVATION_REQUEST_STEPS = [
  'Descriptif du circuit',
  'Demande de réservation',
  'Tarifs',
];

interface ReservationRequestPageProps {
  estimateResponse: EstimateResponse;
}

const ReservationRequestPage = ({
  estimateResponse,
}: ReservationRequestPageProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const [nbAdults, setNbAdults] = useState(estimateResponse.adults.length);
  const [nbChildren, setNbChildren] = useState(
    estimateResponse.children.length,
  );
  const [childrenBirthDates, setChildrenBirthDates] = useState<
    Record<number, Date>
  >(estimateResponse.children.map(({ birthDate }) => new Date(birthDate)));

  const parsedEstimate = useMemo(
    () =>
      loadEstimateDefaultValues({
        estimateResponse,
        t,
        l: currentLanguage,
      }),
    [currentLanguage, estimateResponse, t],
  );

  const methods = useForm<EstimateFormData>({
    mode: 'onChange',
    resolver: zodResolver(EstimateSchema),
    defaultValues: parsedEstimate,
  });

  const { control } = methods;

  const defaultSelectedOptions = useWatch({
    control,
    name: 'selectedEstimateOptions',
  });

  const childrenAges = useMemo(
    () =>
      Object.values(childrenBirthDates).map((birthdate) =>
        differenceInYears(startOfToday(), birthdate),
      ),
    [childrenBirthDates],
  );

  const [currStep, setCurrStep] = useState(0);

  const parsedCircuit = useMemo(
    () => parsedEstimate.circuit,
    [parsedEstimate.circuit],
  );

  const selectedArrivalDate = useMemo(
    () =>
      estimateResponse.arrivalDate
        ? new Date(estimateResponse.arrivalDate)
        : null,
    [estimateResponse.arrivalDate],
  );

  const targetDates = useMemo(() => {
    if (!selectedArrivalDate || !parsedCircuit.nbDays) return undefined;
    return Array.from({ length: parsedCircuit.nbDays }, (_, i) =>
      addDays(selectedArrivalDate, i),
    );
  }, [selectedArrivalDate, parsedCircuit.nbDays]);

  const { findMinimumPriceForDate, daysTrackingMultiDayBenefits } =
    useCircuitPrices({
      days: parsedCircuit.circuitDays,
      circuitMarginRate: parsedCircuit.marginRate,
      nbParticipants: nbAdults + nbChildren,
      nbChildren,
      childrenAges,
      creationDate: new Date(estimateResponse.createdAt),
      targetCurrency: estimateResponse.EstimateCircuit.circuit.currencies[0],
      targetDates,
    });

  const parsedCircuitWithMultiDayBenefitTracking = useMemo(
    () => ({ ...parsedCircuit, circuitDays: daysTrackingMultiDayBenefits }),
    [parsedCircuit, daysTrackingMultiDayBenefits],
  );

  const pricesByStartDate = useMemo(
    () =>
      new Map(
        getDatesToPrice(targetDates).map((date) => [
          date.toISOString(),
          findMinimumPriceForDate(date),
        ]),
      ),
    [findMinimumPriceForDate, targetDates],
  );

  const totalPriceForAll = useMemo(
    () =>
      roundUpPrice(
        Array.from(pricesByStartDate.values())
          .filter((price) => price > 0)
          .reduce((a, b) => Math.min(a, b), Infinity),
      ),
    [pricesByStartDate],
  );

  const totalPrice = useMemo(
    () => roundUpPrice(totalPriceForAll / (nbAdults + nbChildren)),
    [nbAdults, nbChildren, totalPriceForAll],
  );

  const daysItinerary = useMemo(
    () =>
      parsedCircuit.circuitDays.map(({ stepTitle, description, images }) => ({
        stepTitle,
        description,
        images: images as string[],
      })),
    [parsedCircuit.circuitDays],
  );

  const images = useMemo(
    () =>
      estimateResponse.EstimateCircuit.circuit.mainImage
        ? [estimateResponse.EstimateCircuit.circuit.mainImage].concat(
            estimateResponse.EstimateCircuit.circuit.images,
          )
        : estimateResponse.EstimateCircuit.circuit.images,
    [
      estimateResponse.EstimateCircuit.circuit.mainImage,
      estimateResponse.EstimateCircuit.circuit.images,
    ],
  );

  const computeOptionalBenefitPrice = useCallback(
    (
      benefit: BenefitFormData,
      date: Date,
      currIndex: number,
      nbDays = 1,
      nbAdultsOverride?: number,
      nbAdolescentsOverride?: number,
      nbChildrenOverride?: number,
      selectedHotelPensionType?: string,
    ) => {
      const { marginRate, prices } = computeBenefitPrices({
        benefit,
        selectedHotelPensionType,
        indexDay: currIndex,
        nbParticipants: nbAdultsOverride
          ? nbAdultsOverride +
            (nbChildrenOverride ?? 0) +
            (nbAdolescentsOverride ?? 0)
          : nbAdults + nbChildren,
        circuitMarginRate: parsedCircuit.marginRate,
        nbChildren: nbChildrenOverride ?? nbChildren,
        nbAdolescents: nbAdolescentsOverride ?? undefined,
        nbDays,
        childrenAges:
          nbChildrenOverride != undefined ? undefined : childrenAges,
        targetDates,
      });
      const rawPrice = prices.get(priceDateKey(date))?.minimumPrice;
      return (rawPrice ?? Infinity) * (1 + marginRate / 100);
    },
    [childrenAges, nbAdults, nbChildren, parsedCircuit.marginRate, targetDates],
  );

  const { reservationDaysObject, reservationDaysTrackingMultiDayBenefits } =
    useReservationSummary({
      nbAdults,
      nbChildren,
      childrenAges,
      parsedCircuit: parsedCircuit as CircuitFormData,
      selectedOptions: defaultSelectedOptions,
      selectedArrivalDate,
    });

  const { mutateAsync: acceptReservationRequest } =
    useAcceptReservationRequest();
  const { mutateAsync: refuseReservationRequest } =
    useRefuseReservationRequest();
  const { toastAsync } = useToast();

  const acceptReservation = useCallback(async () => {
    const { status } = await toastAsync(
      acceptReservationRequest(estimateResponse.id),
      {
        success: 'Demande de réservation acceptée',
      },
    );
    if (status >= 200 && status < 300)
      navigate(PATHS.home.reservations.requests.list);
  }, [acceptReservationRequest, estimateResponse.id, navigate, toastAsync]);

  const refuseReservation = useCallback(async () => {
    const { status } = await toastAsync(
      refuseReservationRequest(estimateResponse.id),
      {
        success: 'Demande de réservation refusée',
      },
    );
    if (status >= 200 && status < 300)
      navigate(PATHS.home.reservations.requests.list);
  }, [estimateResponse.id, navigate, refuseReservationRequest, toastAsync]);

  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, SelectedEstimateOption>
  >(defaultSelectedOptions as Record<string, SelectedEstimateOption>);

  const creationDate = useMemo(
    () => new Date(estimateResponse.createdAt),
    [estimateResponse.createdAt],
  );

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={parsedCircuit.name}
          code={formatEstimateRef(estimateResponse.ref)}
          breadcrumb={
            <StyledButton
              variant="link"
              title={t('pages.reservationRequest.backToList')}
              className="text-sm text-[#3D3D3D] pl-0"
              onClick={() => navigate(PATHS.home.reservations.requests.list)}
              icon={<ArrowLeftIcon />}
            />
          }
          subHeading={estimateResponse.EstimateCircuit.circuit.countries
            .map((country) => getTranslatedCountry(country, currentLanguage))
            .join(', ')}
          subTitle={t('circuits.duration', {
            days: parsedCircuit.nbDays,
            nights: parsedCircuit.nbNights,
          })}
        ></StyledFormHeaderWithBreadcrumb>
        <HorizontalLine />
        <div className="mt-8">
          <StyledStepper
            steps={RESERVATION_REQUEST_STEPS}
            activeStep={currStep}
            setStep={setCurrStep}
          />
        </div>
        <HorizontalLine />
        {currStep === 0 ? (
          <div className="flex flex-col lg:flex-row-reverse gap-4 p-6">
            <div className="w-fit h-fit lg:sticky lg:top-20">
              <CircuitDetailsParticipantsCard
                minimumPrice={totalPrice}
                nbAdults={nbAdults}
                setNbAdults={setNbAdults}
                nbChildren={nbChildren}
                setNbChildren={setNbChildren}
                childrenBirthDates={childrenBirthDates}
                setChildrenBirthDates={setChildrenBirthDates}
                defaultNbParticipants={nbAdults + nbChildren}
                selectedArrivalDate={selectedArrivalDate}
                mainBtnLabel="Voir la demande de réservation"
                onSelectOptionsButtonClick={() => {
                  setCurrStep(1);
                  window.scrollTo(0, 0);
                }}
                shouldShowParticipantsSetterButtons={false}
                currency={
                  estimateResponse.EstimateCircuit.circuit.currencies[0]
                }
                isViewMode
              />
            </div>
            <div className="flex flex-col gap-4 w-full">
              <CircuitDetailsImagesCard
                circuitId={estimateResponse.EstimateCircuit.circuit.id}
                images={images}
              />
              <CircuitDetailsSummaryCard
                summary={parsedCircuit.summary ?? ''}
                title={t('circuits.editorial.details.summary')}
              />
              <CircuitDetailsSummaryCard
                summary={parsedCircuit.importantPoints ?? ''}
                title={t('circuits.editorial.details.importantPoints')}
              />
              <CircuitDetailsItineraryCard itinerary={daysItinerary} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <CircuitDetailsSummaryCard
                  title={t('circuits.editorial.details.priceIncludes')}
                  summary={parsedCircuit.priceIncludes ?? ''}
                  textBrand
                />
                <CircuitDetailsSummaryCard
                  title={t('circuits.editorial.details.priceExcludes')}
                  summary={parsedCircuit.priceExcludes ?? ''}
                  textBrand
                />
                <CircuitDetailsSummaryCard
                  title={t('circuits.editorial.details.modificationConditions')}
                  summary={parsedCircuit.modificationsConditions ?? ''}
                  textBrand
                />
                <CircuitDetailsSummaryCard
                  title={t('circuits.editorial.details.cancellationConditions')}
                  summary={parsedCircuit.cancellationConditions ?? ''}
                  textBrand
                />
              </div>
            </div>
          </div>
        ) : selectedArrivalDate && currStep === 1 ? (
          <EstimateDetailsAndOptions
            circuit={
              parsedCircuitWithMultiDayBenefitTracking as CircuitFormData
            }
            arrivalDate={selectedArrivalDate}
            adults={nbAdults}
            children={nbChildren}
            totalPrice={
              pricesByStartDate.get(selectedArrivalDate.toISOString()) ?? 0
            }
            control={control}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            estimateMarginRate={0}
            computeOptionalBenefitPrice={computeOptionalBenefitPrice}
            goToLastStep={() => {
              setCurrStep(2);
              window.scrollTo(0, 0);
            }}
            saveEstimate={() => {}}
            sendReservationRequest={() => {}}
            status={estimateResponse.status}
            currency={estimateResponse.EstimateCircuit.circuit.currencies[0]}
            creationDate={creationDate}
            language={currentLanguage}
            isViewMode
            isContactFormDisabled
            isIncomingReservation
          />
        ) : (
          currStep === 2 &&
          selectedArrivalDate && (
            <ReservationSummaryPage
              reservationDaysObject={reservationDaysObject}
              reservationDaysTrackingMultiDayBenefits={
                reservationDaysTrackingMultiDayBenefits
              }
              selectedArrivalDate={selectedArrivalDate}
              selectedOptions={defaultSelectedOptions}
              status={estimateResponse.status}
              onAccept={acceptReservation}
              onRefuse={refuseReservation}
              adults={estimateResponse.adults}
              children={estimateResponse.children}
              currency={estimateResponse.EstimateCircuit.circuit.currencies[0]}
              creationDate={creationDate}
              language={currentLanguage}
            />
          )
        )}
      </StyledContainer>
    </FormProvider>
  );
};
